import { Component, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from '../../services/workflow/workflow.service';
import { hardLegallyBindingFilesArrayValidators } from '../workflow-file-upload-validators';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeRadioGroupComponent } from '../../payee-radio/payee-radio-group/payee-radio-group.component';
import { MatError } from '@angular/material/input';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';
import { PayeeWorkflowProvidedMediaComponent } from '../../payee-workflow-provided-media/payee-workflow-provided-media.component';
import { PayeeDropzoneComponent } from '../../payee-dropzone/payee-dropzone.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { TranslateModule } from '@ngx-translate/core';

type EnforceableOption = {
  value: string;
  label: string;
};

type DialogData = {
  transition_label: string;
  options: EnforceableOption[];
};

@Component({
  selector: 'app-hard-legally-binding-dialog',
  templateUrl: './hard-legally-binding-dialog.component.html',
  styleUrls: ['./hard-legally-binding-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeRadioGroupComponent,
    MatError,
    PayeeFormFieldComponent,
    PayeeInputDirective,
    PayeeWorkflowProvidedMediaComponent,
    PayeeDropzoneComponent,
    PayeeIconComponent,
    PayeeIconButtonComponent,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class HardLegallyBindingDialogComponent implements OnInit {
  private dialogRef =
    inject<
      MatDialogRef<HardLegallyBindingDialogComponent, RunTransition | undefined>
    >(MatDialogRef);
  readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private workflowService = inject(WorkflowService);
  private fb = inject(FormBuilder);

  loading = false;
  dialogData?: DialogData;

  readonly form = this.fb.group({
    enforceable_option: this.fb.control<EnforceableOption | null>(
      null,
      Validators.required,
    ),
    files: this.fb.array<File | null>(
      [],
      hardLegallyBindingFilesArrayValidators(this.data.media),
    ),
    message: this.fb.control<string | null>(null, Validators.maxLength(1000)),
  });

  get enforceableOption() {
    return this.form.controls.enforceable_option;
  }
  get files() {
    return this.form.controls.files;
  }
  get message() {
    return this.form.controls.message;
  }

  get selectedValue(): string {
    return this.enforceableOption.value?.value ?? '';
  }

  get dropzoneTitle(): string {
    if (!this.dialogData?.transition_label) {
      return '';
    }
    const parts = this.dialogData.transition_label.split('|');
    return parts.length > 1
      ? parts.slice(1).join('|').trim()
      : this.dialogData.transition_label;
  }

  async ngOnInit() {
    this.dialogData = (await this.workflowService.getDialogData({
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
    })) as DialogData;

    if (this.dialogData.options.length > 0) {
      this.enforceableOption.patchValue(this.dialogData.options[0]);
    }
  }

  displayWith(option: EnforceableOption): string {
    return option.label;
  }

  onDrop(files: FileList) {
    if (this.files.length >= 10) {
      return;
    }

    this.files.markAsDirty();
    for (let i = 0; i < files.length; ++i) {
      this.files.push(this.fb.control(files.item(i)));
      if (this.files.length >= 10) {
        this.files.disable();
        return;
      }
    }
  }

  removeFile(i: number) {
    this.files.removeAt(i);
    if (this.files.disabled && this.files.length <= 10) {
      this.files.enable();
      return;
    }
  }

  readonly cancel = () => {
    this.dialogRef.close();
  };

  readonly submit = async () => {
    this.form.markAllAsTouched();

    if (!this.dialogData || this.form.invalid) {
      return;
    }

    const {
      enforceable_option: option,
      files,
      message,
    } = this.form.getRawValue();
    if (!option) {
      return;
    }

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params: {
        ...(this.data.extraParams ?? {}),
        enforceable_option: option.value,
        files: [
          ...(this.data.media ?? []).map((m) => m.uuid),
          ...files.filter((f): f is File => f !== null),
        ],
        message,
      },
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };
}
